/** @file A lerped drawable: point, one-point turn and second point blended toward another node (21.7). */
import type { DrawableLerp } from '@zilo/pose3d-format/types/drawable';
import { lerpAngleDeg } from '@zilo/pose3d-math/angle/lerpAngleDeg';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { wrapSignedDegrees } from '@zilo/pose3d-render-core/camera/wrapSignedDegrees';

/** One node's placement of a drawable: rounded point A, one-point turn theta, second point B (two-point modes). */
export type NodePlacement = { a: Vec3; theta: number; b: Vec3 | undefined };

const mix = (p: Vec3, q: Vec3, t: number): Vec3 => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t, p[2] + (q[2] - p[2]) * t];

/**
 * `A = A_n + (A_m - A_n) w` and `theta = lerpAngle(theta_n, theta_m, w)` (shortest way, wrapped to [-180, 180)), neither
 * re-rounded; `B = B_n + (B_m - B_n) wB` (`wB ?? w`). With `wB = w` the segment keeps the blended length:
 * `L = lerp(|A_n - B_n|, |A_m - B_m|, w)` measured with z divided by `zScaleRotate` (the factor is already in each B),
 * and A moves along `A - B` to `|A - B| = L`. When either node has no second point, B stays B_n.
 */
export function lerpPlacementOf(n: NodePlacement, m: NodePlacement, lerp: DrawableLerp, zScaleRotate: number): NodePlacement {
  const { w } = lerp;
  const wB = lerp.wB ?? w;
  const a = mix(n.a, m.a, w);
  const theta = wrapSignedDegrees(lerpAngleDeg(n.theta, m.theta, w));
  if (n.b === undefined || m.b === undefined) return { a, theta, b: n.b };
  const b = mix(n.b, m.b, wB);
  if (wB !== w) return { a, theta, b };
  const len = (p: Vec3, q: Vec3): number => Math.hypot(p[0] - q[0], p[1] - q[1], (p[2] - q[2]) / zScaleRotate);
  const target = len(n.a, n.b) + (len(m.a, m.b) - len(n.a, n.b)) * w;
  const now = len(a, b);
  if (now < 1e-12) return { a, theta, b };
  const k = target / now;
  return { a: [b[0] + (a[0] - b[0]) * k, b[1] + (a[1] - b[1]) * k, b[2] + (a[2] - b[2]) * k], theta, b };
}
